import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { AlertCircle, Building2, Lock, Mail, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';

const PRACTIQ_LOGO = "https://customer-assets.emergentagent.com/job_61ca5ff4-bcc2-4cdc-8453-7407936ace18/artifacts/6nklllpo_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

const OrgLoginPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { organization, orgLogin, setOrgBySlug, loading: orgLoading } = useOrg();
  const { login: authLogin } = useAuth();
  
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orgData, setOrgData] = useState(null);

  // Fetch org data on mount
  useEffect(() => {
    const fetchOrg = async () => {
      if (slug) {
        const data = await setOrgBySlug(slug);
        setOrgData(data);
      }
    };
    fetchOrg();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await orgLogin(formData.email, formData.password);
      
      if (result.success) {
        // Also update main auth context
        navigate(`/${slug}/gst`);
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state
  if (orgLoading) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Show error if org not found
  if (!orgData && !orgLoading) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center px-4">
        <Card className="bg-[#111827] border-white/10 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Organization Not Found</h2>
            <p className="text-gray-400 mb-6">
              The organization "{slug}" doesn't exist or is not active.
            </p>
            <Button onClick={() => navigate('/')} className="bg-teal-500 hover:bg-teal-400">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go to Main Site
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      {/* Co-branded Header */}
      <div className="w-full bg-[#111827] border-b border-white/10 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* PractiQ Logo */}
            <img src={PRACTIQ_LOGO} alt="PractiQ" className="w-10 h-10 object-contain" />
            
            {/* X separator */}
            <span className="text-2xl text-gray-600 font-light">×</span>
            
            {/* Organization Logo/Name */}
            {orgData?.logo_url ? (
              <img 
                src={orgData.logo_url} 
                alt={orgData.name} 
                className="h-10 object-contain"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Building2 className="w-8 h-8 text-teal-400" />
                <span className="text-xl font-semibold text-white">{orgData?.name}</span>
              </div>
            )}
          </div>
          
          <Badge 
            className="border-0"
            style={{ backgroundColor: `${orgData?.primary_color}20`, color: orgData?.primary_color }}
          >
            Partner Portal
          </Badge>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex items-center justify-center px-4 py-16">
        <Card className="bg-[#111827] border-white/10 w-full max-w-md">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">
              Welcome to {orgData?.name}
            </CardTitle>
            <CardDescription className="text-gray-400">
              Sign in with your registered email to access the tax simulator
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6">
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-500"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Use the email registered by your organization
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white font-medium"
                style={orgData?.primary_color ? {
                  background: `linear-gradient(to right, ${orgData.primary_color}, ${orgData.primary_color}dd)`
                } : {}}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-gray-500">
                Not part of {orgData?.name}?
              </p>
              <Button
                variant="link"
                onClick={() => navigate('/')}
                className="text-teal-400 hover:text-teal-300"
              >
                Go to main PractiQ site →
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 w-full bg-[#080C16] border-t border-white/5 py-3 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-sm">
          <span className="text-gray-600">Powered by PractiQ - TaxSetu AI</span>
          <span className="text-gray-600">© 2025 All rights reserved</span>
        </div>
      </div>
    </div>
  );
};

export default OrgLoginPage;
